import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateTimeParts } from '../format-date';
import { holds, useExpensesPermissions } from '../use-expenses-permissions';
import type { ReportDecision, RowDecision } from './api-decisions';
import { DecisionPanel } from './DecisionPanel';
import { DecisionSheet } from './DecisionSheet';
import type { DecisionError } from './use-decisions';
import type { Report, ReportExpenseRow } from './types';

export const EXPENSES_APPROVE = 'expenses:report:approve';

/**
 * §1.5: the first decision, above the table, while the report waits at the 2nd level: who
 * approved, when, and the note when there is one (`firstApproval`); `firstApprover` names them
 * when an older server sends no `firstApproval`.
 */
export function FirstDecision({ report }: { readonly report: Report }) {
  const { t } = useTranslation('expenses');
  if (report.status !== 'submitted' || report.approvalStage !== 2) return null;
  const approval = report.firstApproval;
  const by = approval?.by ?? report.firstApprover;
  if (!by) return null;
  const when = approval?.at ? dateTimeParts(approval.at) : null;
  const note = approval?.note?.trim();
  const key = note ? 'firstLevelOnWith' : 'firstLevelOn';
  const text = when
    ? t(`reports.page.decision.${key}`, {
        name: by.displayName,
        date: when.date,
        time: when.time,
        note,
      })
    : t('reports.page.decision.firstLevel', { name: by.displayName });
  return (
    <p
      className="border-border bg-muted/50 rounded-lg border p-3 text-sm"
      data-testid="first-decision"
    >
      {text}
    </p>
  );
}

/**
 * The line of §1.4 for someone who may approve but whose own report this is: at any level, while
 * the report waits. The API says it with `can.owned`.
 */
export function OwnReportLine({ report }: { readonly report: Report }) {
  const { t } = useTranslation('expenses');
  const permissions = useExpensesPermissions();
  if (
    report.status !== 'submitted' ||
    report.can?.owned !== true ||
    !holds(permissions, EXPENSES_APPROVE)
  ) {
    return null;
  }
  return (
    <p role="status" className="text-muted-foreground text-sm" data-testid="own-report-line">
      {t('reports.page.decision.ownReport')}
    </p>
  );
}

export function DecisionMessages({
  error,
  done,
}: {
  readonly error: DecisionError | null;
  readonly done: ReportDecision | null;
}) {
  const { t } = useTranslation('expenses');
  if (error) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {t(`reports.page.decision.errors.${error}`)}
      </p>
    );
  }
  if (!done) return null;
  return (
    <p role="status" className="text-sm font-semibold">
      {t(`reports.page.decision.done.${done}`)}
    </p>
  );
}

type RowAsk = { readonly row: ReportExpenseRow; readonly kind: RowDecision };

/** The note a row's «Rifiuta» or «Rendi rimborsabile…» asks for, then the decision. */
export function useRowAsk(
  busy: boolean,
  onDecide: (kind: RowDecision, expenseId: string, note: string) => Promise<void>,
) {
  const { t } = useTranslation('expenses');
  const [ask, setAsk] = useState<RowAsk | null>(null);
  const sheet = ask ? (
    <DecisionSheet
      title={t(`reports.page.decision.${ask.kind === 'reject' ? 'rejectTitle' : 'makeTitle'}`)}
      help={t(`reports.page.decision.${ask.kind === 'reject' ? 'rejectHelp' : 'makeHelp'}`)}
      confirmLabel={t(
        `reports.page.decision.${ask.kind === 'reject' ? 'rejectExpense' : 'makeReimbursable'}`,
      )}
      destructive={ask.kind === 'reject'}
      busy={busy}
      onClose={() => setAsk(null)}
      onConfirm={(note) => void onDecide(ask.kind, ask.row.id, note).then(() => setAsk(null))}
    />
  ) : null;
  return { sheet, open: (row: ReportExpenseRow, kind: RowDecision) => setAsk({ row, kind }) };
}

export function DecisionFoot({
  report,
  busy,
  onDecide,
}: {
  readonly report: Report;
  readonly busy: boolean;
  readonly onDecide: (kind: ReportDecision, note: string) => void;
}) {
  const can = report.can ?? {};
  if (can.approve !== true && can.sendBack !== true && can.refuse !== true) {
    return <OwnReportLine report={report} />;
  }
  return <DecisionPanel report={report} busy={busy} onDecide={onDecide} />;
}
